import { EMAIL_TABLE } from "./resources/communications/email/db"
import { isDbClosedError } from "./is-db-closed-error"
import type { HttpClientAdapter, StorageAdapter } from "./types"

/**
 * Open-before-backfill email BODY fetch (STK-1325).
 *
 * The email list syncs metadata-only (bodies backfill in the background). When
 * the user opens an email whose body hasn't backfilled yet, this fetches the
 * full row (`GET /emails/:id`, with body) and merges it into the metadata row
 * already in Dexie — the reactive `useEmail` read then re-fires with the body.
 *
 * Offline / fetch failure → `{ offlineMiss: true }` with no writes (the view
 * shows a loader / offline hint). Respects the change-queue shield so a body
 * merge never clobbers a pending optimistic edit on the same row.
 */

const EMAIL_BY_ID_URL = "/api/v2/emails"

export interface OnDemandEmailDeps {
  storage: StorageAdapter
  httpClient: HttpClientAdapter
  getActiveEntityIds: (resource: string) => Promise<Set<string>>
  isOnline: () => boolean
}

export interface FetchEmailBodyResult {
  hydrated: boolean
  offlineMiss: boolean
}

export async function fetchEmailBody(
  emailId: string,
  deps: OnDemandEmailDeps
): Promise<FetchEmailBodyResult> {
  if (!deps.isOnline()) return { hydrated: false, offlineMiss: true }

  let full: { id: string } & Record<string, unknown>
  try {
    full = await deps.httpClient.get<{ id: string } & Record<string, unknown>>(
      `${EMAIL_BY_ID_URL}/${emailId}`
    )
  } catch {
    return { hydrated: false, offlineMiss: true }
  }

  // Read the current row FIRST, then re-check the change-queue shield
  // immediately before writing. The storage read is an async gap during which
  // an optimistic edit could be enqueued; checking the shield after it (with no
  // further await before the `put`) guarantees a body merge never clobbers a
  // pending local change.
  const existing = await deps.storage.get<Record<string, unknown>>(
    EMAIL_TABLE,
    emailId
  )
  const shield = await deps.getActiveEntityIds("emails")
  if (shield.has(emailId)) return { hydrated: false, offlineMiss: false }

  // Merge the authoritative full row over the metadata row already in Dexie
  // (never drops metadata — the full row carries it too).
  try {
    await deps.storage.put(
      EMAIL_TABLE,
      existing ? { ...existing, ...full } : full
    )
  } catch (err) {
    // Store closed mid-write (cross-tab logout/delete). Body hydration is a
    // read-through cache fill, not user data — return a clean miss the view can
    // retry instead of an unhandled `DatabaseClosedError`.
    if (isDbClosedError(err)) return { hydrated: false, offlineMiss: true }
    throw err
  }
  return { hydrated: true, offlineMiss: false }
}
